/**
 * Act 1, beat 5 — ***The Cull Corridor***.
 *
 * Spec §7: *"The game's centre of gravity. Near-monochrome, one red lamp,
 * plastic sheeting, shapes behind it, a machine's rhythm. She must cross it. No
 * enemy, no threat mechanic — just the crossing. She hides in a bin and is
 * carried through by a worker who never looks down."*
 *
 * ── The one sentence this beat has to deliver ────────────────────────────────
 *
 * **She crosses it, and nothing stops her, and that is the worst thing that has
 * happened in the game so far.**
 *
 * Beat 4 was the exam: three patrols, a dog, a door built for boots. This beat
 * takes the exam away. Nothing in this room is looking for her, there is no
 * suspicion to manage and no cover to reach, and the player will not believe any
 * of that for the first thirty seconds. The beat is built out of the gap between
 * what they are doing — walking down a corridor — and what they are certain is
 * about to happen.
 *
 * Their own trained reflexes supply the horror. They will crouch, wait for the
 * machine's cycle and move between thumps, and none of it was ever necessary.
 * **The one thing this beat must never do is reward that.** No near miss, no
 * "you made it", no stage that advances because she timed something. The machine
 * keeps its rhythm whether she moves or not.
 *
 * ── Every stage has a backstop, and this paragraph used to say otherwise ─────
 *
 * It said there was no patience clock on the crossing, that the beat's only
 * backstop was `BIN_PATIENCE`, and that the bin was "the only place in the beat
 * a player can be genuinely stuck". All three were wrong. `CROSSING_PATIENCE`
 * and `BAY_PATIENCE` were already here when it was written, and the place a
 * player could genuinely get stuck was the **first** stage, not the last: it
 * waited on a flag raised by walking, with nothing to give up, so a player who
 * pressed nothing sat in the entrance for the rest of the session. See
 * `ARRIVE_PATIENCE`.
 *
 * The lesson is not about this beat. **A comment asserting a safety property is
 * not a gate**, and this one was load-bearing enough that it stopped anybody
 * looking. Spec §10 says every stage can be waited out; every stage in the list
 * below now can be, and `cull-corridor.spec.ts`'s no-input run is what says so.
 *
 * ── Nothing here touches the world ───────────────────────────────────────────
 *
 * Stages emit **cues**; `cullDirector.ts` is the only file that knows any of this
 * is triangles, sheeting or a bin. Same rule as every beat since `born.ts`: if a
 * stage in this file ever imports Three, the beat has stopped being replayable in
 * Node and the mistake will be here rather than in the sequencer.
 *
 * **The spatial stages wait on flags rather than reading `ctx.self` against the
 * room's constants**, for the reason `otto.ts` gives: "she is level with the
 * sheeting" is a fact about a room, and the room decides it.
 */

import type { Beat, Stage, StoryContext } from '@/story/types';

/**
 * Interactable ids. There is one, and that is the design.
 *
 * Beat 4 had two — a thing to read and a thing to do. This beat has one thing to
 * do and one thing that is *offered and never required*, and only the first of
 * them is an id a stage waits on. The shapes behind the sheeting are the other
 * one: see `CULL_IDS.bay`.
 */
export const CULL_IDS = {
  /**
   * The bin, under `nudge`, held.
   *
   * Not a new verb. Spec §5 has no "climb in", and inventing one for a single
   * shot in a single beat would be the most expensive way to say something the
   * existing grammar already says: she puts her snout against a thing and pushes
   * until something happens, which is how she has opened every door in the game.
   */
  bin: 'cull.bin',
  /**
   * The bay, under `observe`, from the corridor.
   *
   * **Optional content, and it is the only optional content in Act 1.** It
   * completes no stage, unlocks nothing and is never prompted twice. Holding
   * focus on the bay composes the shot the player has been avoiding: the rail,
   * the shapes, the cold light behind the plastic.
   *
   * That is the whole of the game's opinion about what is happening in this
   * building, and spec §2's content restraint is why it is offered rather than
   * delivered — *silhouette, sound, absence and implication*, and the player's
   * own decision to look.
   */
  bay: 'cull.bay',
} as const;

/** Story flags the GAME raises. All of them are things only the simulation knows. */
export const CULL_FLAGS = {
  /** She is far enough in that the boot-wash door is behind her. */
  inCorridor: 'cull.inCorridor',
  /** She is level with the sheeting — the middle of the room, and the middle of the beat. */
  atTheBay: 'cull.atTheBay',
  /** She is past the bay and into the red. */
  pastTheBay: 'cull.pastTheBay',
  /**
   * The bin is on its side, and the hole in the world is open.
   *
   * Raised by the game when the topple has *finished*, not when the shove
   * completed. A stage that advanced on the verb would offer her a way into a
   * bin that was still falling.
   */
  binDown: 'cull.binDown',
  /**
   * She is in the bin.
   *
   * Raised by the game when her body is actually inside `cullBinInterior()` —
   * not when an interactable completes and not when a director sets a flag. The
   * difference is D-090's lesson for the third time: a flag that describes a
   * moment cannot be used to gate a thing the world has to have finished doing.
   */
  inTheBin: 'cull.inTheBin',
  /** The ride is over and the bin is through the door. */
  throughTheDoor: 'cull.throughTheDoor',
} as const;

/** Every cue this beat can emit. The director switches on these and nothing else. */
export const CULL_CUES = {
  /** The door shuts behind her. Cold green to near-monochrome, and one red lamp ahead. */
  open: 'cull.open',
  /**
   * The machine takes its first cycle, thump first, and from here it runs for
   * the rest of the beat.
   *
   * **Not "never stops"**, which is what this line used to say and which the
   * director took literally: `#running` was set here and cleared nowhere, so the
   * plant went on thumping behind the black screen after `end`, forever. The
   * fiction is that the machine never stops. The simulation of it stops when the
   * beat does. See `CULL_CUES.end`.
   */
  firstCycle: 'cull.firstCycle',
  /** The crossing proper. */
  crossing: 'cull.crossing',
  /** She is level with the mouth. The lift is bigger here and the bay is lit. */
  theBay: 'cull.theBay',
  /** `observe` becomes available on the bay. Offered once. */
  offerBay: 'cull.offerBay',
  /** The far end, the red, and a door that does not open for her. */
  theDoor: 'cull.theDoor',
  /** `nudge` becomes available on the bin. */
  offerBin: 'cull.offerBin',
  /**
   * Knock the bin over, whether or not she found the verb.
   *
   * The gentler of this beat's two backstops: it opens the way in and leaves
   * walking into it to her. See `TIP_PATIENCE`.
   */
  tipItOver: 'cull.tipItOver',
  /**
   * Put her in the bin, whether or not she worked it out.
   *
   * The hard one, and the beat is not embarrassed by it. See `IN_PATIENCE`.
   */
  putHerIn: 'cull.putHerIn',
  /** The lid, the man, and the whole room going past sideways. */
  carried: 'cull.carried',
  /** Fade to black. */
  fadeOut: 'cull.fadeOut',
  /** The beat is over. */
  end: 'cull.end',
} as const;

/**
 * The bin is now TWO stages, and each of them has its own backstop.
 *
 * ── Why it is two ───────────────────────────────────────────────────────────
 *
 * It was one: `nudge` the bin and be inside it. Alex played that and called it
 * unrealistic, and the diagnosis is in `CULL_BIN` — a 0.32 m pig was teleported
 * into a 1.15 m bin, which is the one thing in this game that ever happened to
 * her body without her body being able to do it.
 *
 * So the verb knocks the bin over, and getting into it is a second thing, done
 * on her own feet, at a hole she is the right size for. That is two waits with
 * two different failure modes — *I did not find the verb* and *I did not realise
 * I could go in* — and spec §10 wants a backstop on each. One clock covering
 * both would time out a player who had done half of it.
 *
 * ── The two numbers ─────────────────────────────────────────────────────────
 *
 * They sum to 90, which is the number the single stage used to carry and the
 * same one beat 3 spends on its roll and its leave. The split is 50/40 rather
 * than 45/45 because finding a verb on an object is the harder half: `nudge` is
 * offered on the bin the way it has been offered on every door in the game, and
 * a player who has not tried it in fifty seconds is not going to. Walking into a
 * hole that has just appeared in front of them needs less.
 *
 * Neither is a punishment clock. **The topple backstop is not even a loss** —
 * the bin goes over in frame, on its own, and the player who was still looking at
 * it gets the same opening a second earlier than they would have. Only
 * `putHerIn` costs anything, and what it costs is the choice, which is the
 * cheapest thing in the beat to lose and the only alternative to a corridor
 * nobody can leave.
 */
const TIP_PATIENCE = 50;
const IN_PATIENCE = 40;

/**
 * How long the crossing may take before the beat stops waiting.
 *
 * Not a patience clock in the discovery sense — there is no verb here to fail to
 * find. It is a backstop against a player who stops walking, and it is long
 * enough that standing still and listening to the room for a full minute is
 * treated as playing the beat correctly, which it is.
 */
const CROSSING_PATIENCE = 75;

/** How long the bay may hold a player who never comes level with it. */
const BAY_PATIENCE = 45;

/** A stage that is purely a length of time with cues at the front of it. */
function hold(id: string, seconds: number, ...cues: readonly string[]): Stage {
  return {
    id,
    enter: (_ctx, emit): void => {
      for (const cue of cues) emit(cue);
    },
    done: (ctx: StoryContext): boolean => ctx.stageTime >= seconds,
  };
}

/** A stage that waits for a flag, or for the player to have had long enough. */
function waitFor(id: string, flag: string, patience: number, ...cues: readonly string[]): Stage {
  return {
    id,
    enter: (_ctx, emit): void => {
      for (const cue of cues) emit(cue);
    },
    done: (ctx: StoryContext): boolean => ctx.completed.has(flag) || ctx.stageTime >= patience,
  };
}

/**
 * How long `arrive` waits for a player who has not moved yet.
 *
 * **This stage used to have no backstop at all, and that was a softlock.** It
 * was a `waitUntil` — "waits for a flag and will not give up" — hung on
 * `CULL_FLAGS.inCorridor`, which `main.ts` raises when she has walked 2.5 m
 * past the spawn. A player who presses nothing never walks 2.5 m, so the flag
 * never arrived and beat 5 stopped dead on its first stage, four stages before
 * the `BIN_PATIENCE` that this beat's whole anti-softlock argument rests on.
 *
 * Spec §10 is the rule and `docs/beat-5-plan.md`'s stage table is the authored
 * intent: `arrive` is *"4 s"*. The flag was added afterwards to make the machine
 * start when she commits to the room rather than on a timer, which is the right
 * instinct — it just has to be the *early* exit and not the only one. Eight
 * seconds is double the authored length, which is what a player standing still
 * in a room they have never seen should get and no more.
 *
 * The gate that catches this is `cull-corridor.spec.ts`'s no-input run. It
 * existed when the beat shipped, and it had never been run.
 */
const ARRIVE_PATIENCE = 8;

export const CULL_CORRIDOR_BEAT: Beat = {
  number: 5,
  id: 'act1.cullCorridor',
  stages: [
    // ── arrive ───────────────────────────────────────────────────────────────
    // Through the boot-wash door from the service corridor. The cut is the
    // content: cold fluorescent green to near-monochrome, 2.6 m of ceiling to
    // 3.0, no haze to 0.35, and one red lamp twenty-five metres away that is the
    // only thing in the frame with a colour in it.
    waitFor('arrive', CULL_FLAGS.inCorridor, ARRIVE_PATIENCE, CULL_CUES.open),

    // ── the first cycle ──────────────────────────────────────────────────────
    // The machine takes a cycle while she is still at the near end, before the
    // sheeting is close enough to look at. **The sound has to be established as
    // part of the building before it is attached to a place**, or the player
    // spends the crossing hunting for what is making it instead of walking
    // toward the thing it is telling them about.
    //
    // Six seconds is one and a half cycles: a thump, a hiss, silence, and then
    // the confirmation that it is going to keep doing that.
    hold('first-cycle', 6, CULL_CUES.firstCycle),

    // ── the crossing ─────────────────────────────────────────────────────────
    // Twelve metres to the mouth of the bay, with nothing in the way.
    //
    // This stage is where the beat either works or is boring, and the only
    // things holding it are the room and the clock: the worklights die behind
    // her, the red does not reach her yet, and every 4.2 s the plastic ahead of
    // her lifts and drops. **Nothing is gated on the rhythm.** She may walk
    // straight down the middle at a trot and nothing will happen, and a player
    // who does that has understood the room faster than one who crawls.
    waitFor('crossing', CULL_FLAGS.atTheBay, CROSSING_PATIENCE, CULL_CUES.crossing),

    // ── the bay ──────────────────────────────────────────────────────────────
    // Level with the mouth. The lift is bigger here — the director doubles it
    // while she is beside it — and `observe` is offered on the bay for as long
    // as she is next to it.
    //
    // The stage ends when she is *past* it, not when she looks: looking is
    // optional (see `CULL_IDS.bay`) and a stage that waited for it would make
    // the game insist on the one thing it has decided to leave to the player.
    waitFor('the-bay', CULL_FLAGS.pastTheBay, BAY_PATIENCE, CULL_CUES.theBay, CULL_CUES.offerBay),

    // ── the door ─────────────────────────────────────────────────────────────
    // Shut, and there is no mat, no gap and no timing trick. D-012 for the
    // fourth time: the farm is built for boots, not for her.
    //
    // **Six seconds of nothing being offered, and they are the point.** The
    // player arrives at a door, tries it, and finds out for themselves that this
    // one is not a puzzle. A prompt that appears before the problem does is a
    // prompt answering a question nobody asked — and it is a stage rather than a
    // timer in the director for the reason every clock in this project is now a
    // stage: a `setTimeout` does not pause when the game does.
    hold('the-door', 6, CULL_CUES.theDoor),

    // ── the bin, and it has to go over before it is anywhere ─────────────────
    // `nudge` is offered on it, and the verb does exactly what the verb has
    // always done: she puts her snout against a thing and shoves until something
    // happens. What happens is that it falls over. Nothing happens to her.
    {
      ...waitFor('the-bin', CULL_FLAGS.binDown, TIP_PATIENCE, CULL_CUES.offerBin),
      exit: (ctx, emit): void => {
        if (!ctx.completed.has(CULL_FLAGS.binDown)) emit(CULL_CUES.tipItOver);
      },
    },

    // ── get in ───────────────────────────────────────────────────────────────
    // There is a hole at floor level now, facing the door, and she is the right
    // size for it. **Nothing is offered here and nothing is prompted**: the
    // opening is the prompt, which is the same argument `the-door` makes six
    // stages earlier in the other direction — a shut door that is not a puzzle,
    // and now an open box that is not a verb.
    //
    // She has to walk around it, because the mouth faces the door. That is the
    // half-metre of navigation that makes getting in a decision rather than a
    // consequence, and it is why the bin topples away from her (see `CULL_BIN`).
    {
      ...waitFor('get-in', CULL_FLAGS.inTheBin, IN_PATIENCE),
      exit: (ctx, emit): void => {
        if (!ctx.completed.has(CULL_FLAGS.inTheBin)) emit(CULL_CUES.putHerIn);
      },
    },

    // ── carried ──────────────────────────────────────────────────────────────
    // Long on purpose, and most of it is a man walking toward you.
    //
    // She is in the bin, lying on its side on the floor. A man comes through the
    // door at the far end and walks the length of the corridor — twelve seconds
    // of it, from a camera across the corridor at bin height — stands the bin
    // back up with her in it, and wheels it out. **He never looks down.** He has
    // no vision cone to look with, and this beat has no watchers at all.
    //
    // The shot is outside the bin and it used to be inside it. See `RIDE_CAMERA`:
    // a man not looking down is the thing this beat is about, and it cannot be
    // photographed from inside the box.
    //
    // Her body stops being hers at the *righting*, not when she got in — which
    // means the last thing the player does in beat 5 is choose to climb into a
    // bin, and the first thing the world does about it is pick the bin up.
    //
    // A `waitFor` rather than a `hold`, because the ride is a thing the world
    // performs and D-090 is the standing rule about stages that hold a clock
    // over work somebody else is doing. The clock is the backstop; the flag is
    // the shot.
    waitFor('carried', CULL_FLAGS.throughTheDoor, 40, CULL_CUES.carried),

    // ── out ──────────────────────────────────────────────────────────────────
    {
      ...hold('close', 5, CULL_CUES.fadeOut),
      exit: (_ctx, emit): void => emit(CULL_CUES.end),
    },
  ],
};
